import { useMemo } from "react"
import * as Validator from "yup"
import {
	Box,
	Button,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	ErrorMessage,
	Form,
	TextInputField,
	useField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { AmenityIcon } from "./AmenityIcons"
import {
	isSameAmenity,
	TAmenity,
	TAmenityCategory,
	TAmenityType,
	useAmenities,
} from "./store"

function AmenityItem({ amenity }: { amenity: TAmenity }) {
	return (
		<Inline gap="2" alignItems="center">
			<AmenityIcon amenityKey={amenity.key} size="5" />
			<Text fontSize="sm">{amenity.name}</Text>
		</Inline>
	)
}

function AmenitiesGroup({
	title,
	amenities,
}: {
	title: string
	amenities: Array<TAmenity>
}) {
	return (
		<Stack gap="3">
			<Text fontWeight="semibold">{title}</Text>
			<Inline gap="4" flexWrap="wrap">
				{amenities.map((amenity) => (
					<AmenityItem key={amenity.key || amenity.name} amenity={amenity} />
				))}
			</Inline>
		</Stack>
	)
}

/**
 * Read-only list of a hotel's or room's amenities with icons, grouped by
 * category. Custom amenities and unknown keys are shown under "Other".
 */
export function AmenitiesList({
	amenities,
	type,
}: {
	amenities: Array<TAmenity>
	type: TAmenityType
}) {
	const { amenityCategories, isFetching } = useAmenities(type)

	if (!amenities.length) {
		return <Text color="muted">No amenities added</Text>
	}

	if (isFetching) {
		return <Text color="muted">Loading amenities...</Text>
	}

	const categories = (amenityCategories || []).filter(
		(category) => category.category !== "Other"
	)
	const groups = categories
		.map((category) => ({
			title: category.category,
			amenities: category.amenities.filter((amenity) =>
				amenities.some((a) => isSameAmenity(a, amenity))
			),
		}))
		.filter((group) => group.amenities.length > 0)
	const otherAmenities = amenities.filter(
		(amenity) =>
			!categories.some((category) =>
				category.amenities.some((a) => isSameAmenity(a, amenity))
			)
	)

	return (
		<Stack gap="6">
			{groups.map((group) => (
				<AmenitiesGroup
					key={group.title}
					title={group.title}
					amenities={group.amenities}
				/>
			))}
			{otherAmenities.length ? (
				<AmenitiesGroup title="Other" amenities={otherAmenities} />
			) : null}
		</Stack>
	)
}

function AmenityChip({
	label,
	selected,
	onClick,
}: {
	label: string
	selected: boolean
	onClick: () => void
}) {
	return (
		<Box
			as="button"
			type="button"
			onClick={onClick}
			display="inlineFlex"
			alignItems="center"
			gap="2"
			paddingX="3"
			paddingY="2"
			rounded="md"
			borderWidth="1"
			borderColor={selected ? "primary_emphasis" : "emphasis"}
			bgColor={selected ? "primary" : "default"}
			color={selected ? "primary" : "default"}
			fontWeight={selected ? "semibold" : "normal"}
			fontSize="sm"
			cursor="pointer"
		>
			{selected ? <Icons.Ok size="4" /> : <Icons.Plus size="4" />}
			{label}
		</Box>
	)
}

/**
 * Pick amenities, grouped by category, for a hotel or a room. Users can also
 * add amenities that are not in the predefined list.
 */
export function AmenitiesField({
	name,
	type,
}: {
	name: string
	type: TAmenityType
}) {
	const {
		input: { value, onChange, onBlur },
	} = useField<Array<TAmenity>>(name, {
		subscription: { value: true },
	})
	const selected = value || []
	const { amenityCategories, isFetching } = useAmenities(type)
	const [isAddOpen, showAdd, hideAdd] = useDialog()

	function isSelected(amenity: TAmenity) {
		return selected.some((a) => isSameAmenity(a, amenity))
	}

	function toggleAmenity(amenity: TAmenity) {
		const next = isSelected(amenity)
			? selected.filter((a) => !isSameAmenity(a, amenity))
			: [...selected, { key: amenity.key, name: amenity.name }]
		onChange(next)
		onBlur()
	}

	function removeCustomAmenity(index: number) {
		onChange(selected.filter((_, i) => i !== index))
		onBlur()
	}

	function addCustomAmenity(name: string) {
		onChange([...selected, { key: null, name }])
		onBlur()
		hideAdd()
	}

	const categories: Array<TAmenityCategory> = amenityCategories || []
	const otherCategory = categories.find((c) => c.category === "Other")

	// selected amenities that are not listed in any category
	const customAmenities = selected
		.map((amenity, index) => ({ ...amenity, index }))
		.filter(
			(amenity) =>
				!categories.some((category) =>
					category.amenities.some((a) => isSameAmenity(a, amenity))
				)
		)

	return (
		<Stack gap="6">
			{isFetching ? (
				<Text color="muted">Loading amenities...</Text>
			) : (
				categories
					.filter((category) => category.category !== "Other")
					.map((category) => (
						<Stack gap="3" key={category.category}>
							<Text fontWeight="semibold" fontSize="lg">
								{category.category}
							</Text>
							<Inline gap="2" flexWrap="wrap">
								{category.amenities.map((amenity) => (
									<AmenityChip
										key={amenity.key || amenity.name}
										label={amenity.name}
										selected={isSelected(amenity)}
										onClick={() => toggleAmenity(amenity)}
									/>
								))}
							</Inline>
						</Stack>
					))
			)}
			<Stack gap="3">
				<Text fontWeight="semibold" fontSize="lg">
					Other
				</Text>
				<Inline gap="2" flexWrap="wrap" alignItems="center">
					{otherCategory?.amenities.map((amenity) => (
						<AmenityChip
							key={amenity.key || amenity.name}
							label={amenity.name}
							selected={isSelected(amenity)}
							onClick={() => toggleAmenity(amenity)}
						/>
					))}
					{customAmenities.map((amenity) => (
						<AmenityChip
							key={amenity.key || amenity.name}
							label={amenity.name}
							selected
							onClick={() => removeCustomAmenity(amenity.index)}
						/>
					))}
					<Button
						type="button"
						level="tertiary"
						inline
						color="primary"
						onClick={showAdd}
					>
						<Icons.Plus size="4" /> Add new Amenity
					</Button>
				</Inline>
			</Stack>
			<ErrorMessage name={name} />
			<AddAmenityDialog
				open={isAddOpen}
				onClose={hideAdd}
				onAdd={addCustomAmenity}
				existingAmenities={selected.concat(
					categories.flatMap((category) => category.amenities)
				)}
			/>
		</Stack>
	)
}

type TNewAmenityFormSchema = {
	name: string
}

/**
 * Add an amenity that is not in the predefined list. It is only added to the
 * field's value; the api stores it with the hotel/room it was added for.
 */
function AddAmenityDialog({
	open,
	onClose,
	onAdd,
	existingAmenities,
}: {
	open: boolean
	onClose: () => void
	onAdd: (name: string) => void
	// the already selected and the predefined ones, none of which can be re-added
	existingAmenities: Array<TAmenity>
}) {
	const validationSchema = useMemo(
		() =>
			Validator.object().shape({
				name: Validator.string()
					.required("Please provide a name")
					.max(100, "Please use 100 or less characters")
					.test(
						"not-a-duplicate",
						"This amenity is already in the list",
						(name) =>
							!existingAmenities.some(
								(amenity) =>
									amenity.name.trim().toLowerCase() ===
									(name || "").trim().toLowerCase()
							)
					),
			}),
		[existingAmenities]
	)
	return (
		<Dialog open={open} onClose={onClose} title="Add new Amenity" sm>
			<Form<TNewAmenityFormSchema>
				initialValues={{ name: "" }}
				validate={validateFormValues(validationSchema)}
				onSubmit={withServerErrors(async (values) => {
					onAdd(values.name.trim())
				})}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body>
							<TextInputField
								name="name"
								label="Name"
								type="text"
								placeholder="Type"
							/>
						</Dialog.Body>
						<Dialog.Footer>
							<Button level="primary" type="submit" disabled={submitting}>
								Add
							</Button>
						</Dialog.Footer>
					</form>
				)}
			</Form>
		</Dialog>
	)
}
